import React, { useState } from 'react';
import { Send, TrendingDown, DollarSign, Target, Shield } from 'lucide-react';

const CostEffectiveSolutions = () => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', company: '', phone: '', email: '',
    facilityType: '', budgetConstraints: '', projectScope: '', currentCosts: '',
    optimizationGoals: '', timeline: '', specificRequirements: '', additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/cost-effective`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Thank you! Your cost-effective solutions inquiry has been submitted successfully. We will contact you shortly.');
        // Reset form
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          facilityType: '', budgetConstraints: '', projectScope: '', currentCosts: '',
          optimizationGoals: '', timeline: '', specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      alert('There was an error submitting your request. Please try again or contact us directly.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const solutions = [
    {
      icon: <DollarSign className="w-8 h-8 text-emerald-700" />,
      title: 'Budget Optimization',
      description: 'Strategic approaches to minimize permitting and compliance costs while maintaining regulatory adherence'
    },
    {
      icon: <Target className="w-8 h-8 text-emerald-700" />,
      title: 'Efficient Strategies',
      description: 'Streamlined processes that reduce time and resources needed for environmental compliance'
    },
    {
      icon: <Shield className="w-8 h-8 text-emerald-700" />,
      title: 'Risk Mitigation',
      description: 'Cost-effective solutions that prevent expensive violations and enforcement actions'
    },
    {
      icon: <TrendingDown className="w-8 h-8 text-emerald-700" />,
      title: 'Long-term Savings',
      description: 'Strategic planning that reduces ongoing operational and compliance costs'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-900 to-emerald-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Cost-Effective Environmental Solutions</h1>
            <p className="text-xl text-emerald-100 mb-8">
              Maximize compliance while minimizing costs. Our strategic approach delivers economical solutions without compromising environmental protection or regulatory adherence.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Our Cost-Effective Approach</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {solutions.map((solution, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow">
                <div className="mb-4">{solution.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                <p className="text-gray-600">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Request Cost-Effective Solutions Support</h2>
            <p className="text-gray-600 mb-8">
              Let us help you achieve compliance goals within your budget constraints
            </p>

            <form onSubmit={handleSubmit}>
              {/* Contact Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              </div>

              {/* Project Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Project Information</h3>
                <select
                  name="facilityType"
                  value={formData.facilityType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 mb-4"
                >
                  <option value="">Facility Type</option>
                  <option value="power">Power Generation</option>
                  <option value="oil-gas">Oil & Gas</option>
                  <option value="military">Military Installation</option>
                  <option value="seafood">Seafood Processing</option>
                  <option value="mining">Mining & Construction</option>
                  <option value="industrial">Industrial</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="text"
                  name="budgetConstraints"
                  value={formData.budgetConstraints}
                  onChange={handleChange}
                  placeholder="Budget constraints or target cost range"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 mb-4"
                />
                <textarea
                  name="projectScope"
                  value={formData.projectScope}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Project scope and compliance needs"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 mb-4"
                ></textarea>
                <input
                  type="text"
                  name="currentCosts"
                  value={formData.currentCosts}
                  onChange={handleChange}
                  placeholder="Current environmental compliance costs (if applicable)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 mb-4"
                />
                <select
                  name="optimizationGoals"
                  value={formData.optimizationGoals}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 mb-4"
                >
                  <option value="">Primary cost optimization goal</option>
                  <option value="reduce-permitting">Reduce Permitting Costs</option>
                  <option value="streamline-compliance">Streamline Compliance Processes</option>
                  <option value="prevent-violations">Prevent Costly Violations</option>
                  <option value="optimize-monitoring">Optimize Monitoring Requirements</option>
                  <option value="long-term-savings">Long-term Operational Savings</option>
                  <option value="overall-reduction">Overall Cost Reduction</option>
                </select>
                <input
                  type="text"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  placeholder="Project timeline"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 mb-4"
                />
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Requirements or Concerns <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="specificRequirements"
                    value={formData.specificRequirements}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="e.g., Budget limitations, specific cost reduction targets, areas needing optimization, compliance challenges, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  ></textarea>
                </div>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Additional information"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-emerald-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-emerald-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSubmitting ? 'Submitting...' : <><Send size={20} /> <span>Submit Cost Solutions Request</span></>}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Choose Our Cost-Effective Approach?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-700 mb-2">13+</div>
              <p className="text-gray-600">Years of Alaska Experience</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-700 mb-2">100%</div>
              <p className="text-gray-600">Compliance Success Rate</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-700 mb-2">30%</div>
              <p className="text-gray-600">Average Cost Savings</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CostEffectiveSolutions;
